import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { switchMap, take } from 'rxjs/operators';
import { AppState } from '../store/app.state';
import { selectUser, selectCurrentTenantId } from '../store/auth/auth.selectors';
import { combineLatest } from 'rxjs';

/** Route pubbliche che non richiedono tenant e non devono attendere lo store. */
const SKIP_TENANT_PATTERNS = /\/invites\/(validate|accept)/;

/** GET lista tenant: non inviare X-Tenant-Id altrimenti il backend lo usa e può fallire (endpoint non richiede contesto tenant). */
const IS_GET_TENANTS_LIST = (url: string) =>
  /\/tenants(\?|$)/.test(url.replace(/^https?:\/\/[^/]+/, ''));

/**
 * Adds X-Tenant-Id header for tenant-scoped APIs.
 * - super_admin: tenant comes from store (currentTenantId). Required for /tenants/current/* (e.g. channels, integrations).
 * - Other roles: tenant comes from user.tenantId.
 */
export const tenantInterceptor: HttpInterceptorFn = (req, next) => {
  if (SKIP_TENANT_PATTERNS.test(req.url)) {
    return next(req);
  }
  if (req.method === 'GET' && IS_GET_TENANTS_LIST(req.url)) {
    return next(req);
  }

  const store = inject(Store<AppState>);

  return combineLatest([store.select(selectUser), store.select(selectCurrentTenantId)]).pipe(
    take(1),
    switchMap(([user, currentTenantId]) => {
      let tenantId: string | null = null;
      if (user) {
        if (user.role === 'super_admin') {
          tenantId = currentTenantId ?? null;
        } else {
          tenantId = user.tenantId ?? null;
        }
      }
      if (tenantId) {
        const clonedReq = req.clone({
          setHeaders: {
            'X-Tenant-Id': tenantId,
          },
        });
        return next(clonedReq);
      }
      return next(req);
    })
  );
};
